import { formatAddress, formatFullAddress, formatPrice } from '@/lib/utils';
import { Property } from '@/types/property';
import { Bath, Bed, DollarSign, MapPin, Square } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import ListingGallery from './ListingGallery';

// Reusable property components
export function PropertyBadge({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <Badge
      className={color ? `bg-${color}-500 text-white` : undefined}
      variant={color ? "default" : "outline"}
    >
      {children}
    </Badge>
  );
}

export function PropertyAddress({ property, variant = 'normal' }: { property: Property | Property; variant?: 'normal' | 'full' }) {
  if (!property.street_number || !property.street_name) return null;

  const address = variant === 'full' ? formatFullAddress(property) : formatAddress(property);

  return (
    <div className="flex items-center text-muted-foreground">
      <MapPin className="h-4 w-4 mr-1 shrink-0 text-gray-500" />
      <p className="truncate text-gray-700">{address}</p>
    </div>
  );
}

export function PropertyPrice({ price, size = 'medium' }: { price: number | null; size?: 'small' | 'medium' | 'large' }) {
  if (price === null) return null;

  const formattedPrice = formatPrice(price);

  const sizeClasses = {
    small: "text-base",
    medium: "text-xl font-bold",
    large: "text-3xl md:text-4xl font-bold"
  };

  return (
    <span className={sizeClasses[size]}>
      {formattedPrice}
    </span>
  );
}

export function PropertyStats({
  beds,
  baths,
  sqft,
  size = 'normal'
}: {
  beds: number;
  baths: number;
  sqft: string | number;
  size?: 'small' | 'normal' | 'large'
}) {
  const sizeConfig = {
    small: {
      container: "flex items-center justify-between text-xs",
      icon: "h-3.5 w-3.5 text-muted-foreground",
      text: "text-xs"
    },
    normal: {
      container: "grid grid-cols-3 gap-1 text-sm",
      icon: "h-4 w-4 text-gray-500",
      text: "text-sm"
    },
    large: {
      container: "flex flex-wrap gap-4 items-center",
      icon: "h-5 w-5 text-muted-foreground",
      text: "text-lg"
    }
  };

  const config = sizeConfig[size];

  return (
    <div className={config.container}>
      <div className="flex items-center gap-1.5">
        <Bed className={config.icon} />
        <span className={config.text}>{beds} Beds</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Bath className={config.icon} />
        <span className={config.text}>{baths} Baths</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Square className={config.icon} />
        <span className={config.text}>{typeof sqft === 'number' ? sqft.toLocaleString() : sqft} sqft</span>
      </div>
    </div>
  );
}

export function PropertyFeatureHighlights({
  features
}: {
  features: string[] | null | undefined;
}) {
  if (!features || features.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {features.slice(0, 4).map((feature) => (
        <Badge
          key={feature}
          variant="secondary"
          className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 py-0.5 px-2"
        >
          {feature}
        </Badge>
      ))}
    </div>
  );
}

export function PropertyStatusBadges({
  property
}: {
  property: Property
}) {
  // Calculate if listing is new
  const listingDate = property.created_at ? new Date(property.created_at) : null;
  const daysAgo = listingDate
    ? Math.floor((new Date().getTime() - listingDate.getTime()) / (1000 * 3600 * 24))
    : null;
  const isNew = daysAgo !== null && daysAgo <= 7;

  if (!isNew && !property.is_waterfront) return null;

  return (
    <div className="absolute left-2 top-2 z-10 flex flex-row gap-1.5 pointer-events-none">
      {isNew && (
        <Badge className="bg-green-500 text-white">New</Badge>
      )}
      {property.is_waterfront && (
        <Badge className="bg-blue-500 text-white">Waterfront</Badge>
      )}
    </div>
  );
}

// Main property display component
interface PropertyDisplayProps {
  property: Property;
  variant: 'card' | 'popup' | 'header';
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  showButton?: boolean;
  buttonText?: string;
}

export default function PropertyDisplay({
  property,
  variant,
  onClick,
  onMouseEnter,
  onMouseLeave,
  showButton = true,
  buttonText = 'View Details'
}: PropertyDisplayProps) {
  // Basic property data formatting
  const beds = property.total_bedrooms || 0;
  const baths = property.total_bathrooms || 0;
  const sqft = property.approx_square_footage
    ? Math.round(property.approx_square_footage).toLocaleString()
    : '0';
  const propertyType = (property.is_freehold ? property.freehold_property_type : property.condo_property_type) || '';

  // Get all property features and take the first few
  const highlightFeatures = [
    ...(property.property_features || []),
    ...(property.interior_features || [])
  ].filter(Boolean).slice(0, 4);

  if (variant === 'card') {
    return (
      <div
        className="overflow-hidden hover:shadow-lg cursor-pointer group relative bg-background rounded-lg border"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        <PropertyStatusBadges property={property} />

        {/* Gallery */}
        <div className="transition-transform group-hover:scale-[1.02]">
          <ListingGallery
            images={property.images || []}
            aspectRatio="4/3"
            className="rounded-t-lg"
          />
        </div>

        <div className="p-3 pb-3 space-y-3.5">
          {/* Price and Type */}
          <div className="flex items-center justify-between mb-1.5">
            <PropertyPrice price={property.list_price} size="medium" />
            {propertyType && <PropertyBadge>{propertyType}</PropertyBadge>}
          </div>

          {/* Location */}
          <PropertyAddress property={property} />

          {/* Features */}
          <PropertyStats beds={beds} baths={baths} sqft={sqft} />

          {/* Property Highlights */}
          <PropertyFeatureHighlights features={highlightFeatures} />
        </div>
      </div>
    );
  }

  if (variant === 'popup') {
    return (
      <div className="w-72 overflow-hidden">
        {property.images && property.images.length > 0 && (
          <ListingGallery
            images={property.images}
            aspectRatio="video"
            small
          />
        )}
        <div className="p-4 space-y-3 bg-background rounded-b-lg">
          <div className="flex items-center gap-2">
            <div className="flex items-center text-lg font-semibold">
              <DollarSign className="h-5 w-5 text-primary mr-1" />
              <PropertyPrice price={property.list_price} size="medium" />
            </div>
            {propertyType && (
              <PropertyBadge>{propertyType}</PropertyBadge>
            )}
          </div>

          <div>
            <PropertyAddress property={property} />
          </div>

          <PropertyStats beds={beds} baths={baths} sqft={sqft} size="small" />

          {showButton && (
            <Button
              size="sm"
              className="w-full"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick?.();
              }}
            >
              {buttonText}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'header') {
    return (
      <div className="space-y-2">
        <PropertyPrice price={property.list_price} size="large" />

        <div className="flex items-center text-xl">
          <PropertyAddress property={property} variant="full" />
        </div>

        <div className="mt-2">
          <PropertyStats beds={beds} baths={baths} sqft={sqft} size="large" />
          {propertyType && (
            <Badge className="text-lg px-2 py-1 mt-2">
              {propertyType}
            </Badge>
          )}
        </div>
      </div>
    );
  }

  // Default fallback
  return null;
}